/**
 * Navigation component — renders sidebar navigation filtered by the user's role.
 *
 * Only shows navigation items that the authenticated user's role is permitted to access.
 * Groups items by category matching the prototype (MONITORAMENTO, RELATÓRIOS, CADASTROS, SISTEMA).
 * Uses Unicode icons matching docs/prototype.html.
 */

import { useRouterState } from '@tanstack/react-router';

import { useAuthStore } from '../stores/auth-store';
import { ROUTE_CONFIG, type RouteEntry } from '../router/route-config';

/**
 * Groups navigation entries by their group field, preserving definition order.
 */
function groupRoutes(routes: RouteEntry[]): Record<string, RouteEntry[]> {
  const groups: Record<string, RouteEntry[]> = {};
  for (const route of routes) {
    (groups[route.group] ??= []).push(route);
  }
  return groups;
}

/**
 * Maps route icon identifiers to Unicode symbols matching the prototype.
 */
const ICON_UNICODE: Record<string, string> = {
  dashboard: '⊞',
  sensor: '◈',
  finance: '◑',
  device: '▣',
  alert: '◬',
  collection: '🚐',
  reconciliation: '⊛',
  reports: '≡',
  caixa: '▨',
  ai: '✦',
  support: '🔧',
  inventory: '📦',
  helpdesk: '💬',
  tag: '◉',
  building: '▦',
  branch: '▤',
  location: '◌',
  people: '▧',
  permissions: '⊕',
  settings: '⚙',
  platform: '⊞',
  ota: '⬆',
  privacy: '⊛',
  audit: '▥',
  developer: '⟨⟩',
  docs: '⟨⟩',
  key: '⚿',
  webhook: '⇋',
  sandbox: '◧',
  log: '▤',
};

/**
 * Group labels in pt-BR matching the prototype exactly.
 */
const GROUP_LABELS: Record<string, string> = {
  monitor: 'MONITORAMENTO',
  relatorio: 'RELATÓRIOS',
  cadastro: 'CADASTROS',
  system: 'SISTEMA',
  developer: 'DEVELOPER',
};

/**
 * Order in which groups appear in the sidebar (matching prototype).
 */
const GROUP_ORDER = ['monitor', 'relatorio', 'cadastro', 'system', 'developer'];

/**
 * Returns navigation items filtered by the current user's role.
 *
 * Excludes:
 * - Routes with path parameters (e.g. /equipamentos/:deviceId) — these are detail views
 * - Routes the user's role is not permitted to access
 */
export function getFilteredNavItems(role: string | null): RouteEntry[] {
  if (!role) return [];

  return ROUTE_CONFIG.filter((route) => {
    // Exclude parameterized routes from navigation
    if (route.path.includes(':')) return false;
    return route.allowedRoles.includes(role as RouteEntry['allowedRoles'][number]);
  });
}

/**
 * Navigation sidebar component.
 *
 * Renders grouped navigation items filtered by the authenticated user's role.
 * Labels are hardcoded in pt-BR to match prototype.
 * Uses TanStack Router state to highlight the active nav item.
 */
/** Versão publicada do portal (spec prod-deploy-bootstrap, emenda de releases por tag). */
export function portalVersion(value: string | undefined = import.meta.env.VITE_APP_VERSION): string {
  return value?.trim() || 'dev';
}

export function Navigation() {
  const role = useAuthStore((s) => s.role);
  const filteredItems = getFilteredNavItems(role);
  const grouped = groupRoutes(filteredItems);

  // Get current pathname from TanStack Router
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });

  return (
    <nav aria-label="Navegação principal" role="navigation">
      {GROUP_ORDER.map((groupKey) => {
        const items = grouped[groupKey];
        if (!items || items.length === 0) return null;

        return (
          <div key={groupKey} className="nav-group">
            <h3 className="nav-group-label">{GROUP_LABELS[groupKey]}</h3>
            <ul role="list">
              {items.map((item) => {
                const icon = ICON_UNICODE[item.icon] ?? '';
                const isActive =
                  item.path === '/'
                    ? currentPath === '/'
                    : currentPath.startsWith(item.path);

                return (
                  <li key={item.path}>
                    <a
                      href={item.path}
                      aria-label={item.label}
                      className={isActive ? 'active' : undefined}
                    >
                      <span className={`nav-icon icon-${item.icon}`} aria-hidden="true">
                        {icon}
                      </span>
                      <span className="nav-label">{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="nav-badge">{item.badge}</span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      <div className="nav-version" data-testid="nav-version" style={{ padding: '8px 16px', fontSize: 11, opacity: 0.6 }}>
        {portalVersion()}
      </div>
    </nav>
  );
}
